// Kalshi USL Trader - Type Definitions

export interface KalshiMarket {
  ticker: string;
  title: string;
  status: 'open' | 'closed' | 'settled';
  last_price: number;
  yes_bid: number;
  yes_ask: number;
  volume: number;
  volume_24h: number;
  open_interest: number;
  result?: 'yes' | 'no';
  // Time fields from Kalshi API
  created_time?: string;
  open_time?: string;
  close_time?: string;
  expiration_time?: string;        // <-- MAIN ONE TO USE (when market settles/resolves)
  latest_expiration_time?: string;
  event_ticker?: string;
}

export interface USLAnalysis {
  logVolume: number;
  certainty: number;
  uslPrediction: number;
  uslResidual: number;
  signal: 'SELL_CERTAINTY' | 'BUY_CERTAINTY' | 'NEUTRAL';
  anomalyScore?: number;
}

export interface Trade {
  id: string;
  timestamp: Date;
  strategy: 'A' | 'B';
  ticker: string;
  title: string;
  marketPrice: number;
  betDirection: 'YES' | 'NO';
  betSize: number;
  cost: number;
  result?: 'win' | 'loss' | 'pending';
  pnl?: number;
  bankrollAfter?: number;
}

export interface StrategyState {
  name: string;
  description: string;
  bankroll: number;
  initialBankroll: number;
  betSizePercent: number;
  trades: Trade[];
  isActive: boolean;
  totalPnL: number;
  winRate: number;
  totalTrades: number;
  wins: number;
  losses: number;
}

export interface TradingConfig {
  mode: 'SIMULATION' | 'LIVE';
  strategyAEnabled: boolean;
  strategyBEnabled: boolean;
  strategyABankroll: number;
  strategyBBankroll: number;
  betSizePercent: number;
  scheduledStart?: string; // HH:MM format
  scheduledEnd?: string;   // HH:MM format
  scheduledDays: number[]; // 0-6, Sunday-Saturday
  autoTrade: boolean;
  refreshIntervalMs: number;
  maxDaysToClose: number;  // Filter markets closing within N days
}

export interface AppState {
  config: TradingConfig;
  strategyA: StrategyState;
  strategyB: StrategyState;
  markets: KalshiMarket[];
  qualifyingMarketsA: KalshiMarket[];
  qualifyingMarketsB: KalshiMarket[];
  isRunning: boolean;
  lastUpdate: Date | null;
  error: string | null;
}

export interface KalshiAPIResponse<T> {
  markets?: T[];
  cursor?: string;
}

export const DEFAULT_CONFIG: TradingConfig = {
  mode: 'SIMULATION',
  strategyAEnabled: true,
  strategyBEnabled: true,
  strategyABankroll: 5.00,
  strategyBBankroll: 5.00,
  betSizePercent: 2,
  scheduledStart: '22:00', // 10pm EST = 03:00 UTC next day
  scheduledEnd: '23:59',
  scheduledDays: [4], // Thursday (best day from analysis)
  autoTrade: false,
  refreshIntervalMs: 30000, // 30 seconds
  maxDaysToClose: 90,  // Default: show markets closing within 90 days
};

export const USL_PARAMS = {
  D: 0.83, // Fitted drift parameter
  SELL_THRESHOLD: 0.1,
  STRONG_SELL_THRESHOLD: 0.2,
  BUY_THRESHOLD: -0.1,
};

export const STRATEGY_A_PARAMS = {
  name: 'The Contrarian',
  minVolume: 100,
  maxVolume: 1000,
  uslResidualMin: 0.1, // Overconfident markets
  betDirection: 'AGAINST' as const,
};

export const STRATEGY_B_PARAMS = {
  name: 'The Value Hunter',
  minVolume: 100,
  maxVolume: 1000,
  minPrice: 20,
  maxPrice: 40,
  betDirection: 'WITH' as const,
};
